import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import type { ProjectPageLoader } from '@modules/project/type/loader';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

interface ProjectBannerProps {
    canDownloadBannerImage: boolean;
    project: ProjectPageLoader['project'];
}

/**
 * Banner component that displays the project banner image
 */
const ProjectBanner = ({
    canDownloadBannerImage,
    project,
}: ProjectBannerProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const bannerRef = useRef<HTMLDivElement>(null);

    const imageUrl = project.Banner?.imageUrl ?? '';
    const videoUrl = project.Banner?.videoBannerUrl;
    const isVideoBanner = project.Banner?.isVideoBanner || !!videoUrl;
    const bannerLibraryImageID = project.Banner.libraryImage?.ID ?? 0;

    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('project');

    useEffect(() => {
        const handleMouseEnter = () => {
            setIsHovered(true);
        };
        const handleMouseLeave = () => {
            setIsHovered(false);
        };

        const bannerElement = bannerRef.current;
        if (bannerElement) {
            bannerElement.addEventListener('mouseenter', handleMouseEnter);
            bannerElement.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (bannerElement) {
                bannerElement.removeEventListener(
                    'mouseenter',
                    handleMouseEnter,
                );
                bannerElement.removeEventListener(
                    'mouseleave',
                    handleMouseLeave,
                );
            }
        };
    }, []);

    if (!imageUrl && !videoUrl) {
        return null;
    }

    const canWrapWithBoost =
        !isVideoBanner && !!imageUrl && bannerLibraryImageID > 0;

    const downloadActionElement =
        canDownloadBannerImage && project.Banner?.downloadLink ? (
            <DownloadAction
                downloadLink={project.Banner.downloadLink}
                canDownload={true}
                libraryImageID={bannerLibraryImageID}
            />
        ) : undefined;

    const bannerContent = (
        <div
            className={cn(
                'group relative aspect-video w-full overflow-hidden bg-black md:max-h-[120vh]',
            )}
        >
            <DetailBannerSection
                {...project.Banner}
                imageUrl={imageUrl}
                title={project.Title}
                dimensions={bannerDimensions}
                size={bannerSize}
                isVideoBanner={isVideoBanner}
                videoBannerUrl={videoUrl ?? ''}
                playsInlineVideoBanner={true}
                autoPlayVideoBanner={true}
                loopVideoBanner={true}
                hideVideoControls={true}
                showBackButton={false}
                awards={project.Awards}
            />

            {project.Banner?.overlay && (
                <div className="z-45 absolute inset-0 bg-black/20" />
            )}

            {!canWrapWithBoost && downloadActionElement && (
                <div
                    className={cn(
                        'absolute bottom-8 right-12 z-50 flex min-h-8 min-w-8',
                    )}
                    style={{
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.1s ease-in-out',
                    }}
                >
                    {downloadActionElement}
                </div>
            )}
        </div>
    );

    return (
        <div className="w-full">
            <div ref={bannerRef} className="relative select-none">
                {canWrapWithBoost ? (
                    <BoostResolutionOverlay
                        libraryImageId={bannerLibraryImageID}
                        currentImageUrl={imageUrl}
                        displayWidth={bannerDimensions.width}
                        displayHeight={bannerDimensions.height}
                        wasUpscaled={!!project.Banner.libraryImage?.WasUpscaled}
                        buttonClassName="bottom-8 right-12 [&>button]:h-12"
                        beforeClassName="top-30"
                        afterClassName="top-30"
                        actionSlot={downloadActionElement}
                    >
                        {bannerContent}
                    </BoostResolutionOverlay>
                ) : (
                    bannerContent
                )}
            </div>
        </div>
    );
};

export default ProjectBanner;
